// vpn-web-bo/src/pages/accounting/lib.ts
//
// 「流量帳務中心」共用型別 + 格式化工具 + 伺服器端 CSV 下載。
// 階段 1（VPN-74 / VPN-75 / VPN-76-lite / VPN-77）共用。
import { authFetch } from "../../utils/api";

/* ───────── API 型別（對齊後端契約 /api/admin/accounting/*） ───────── */

/** VPN-74 總覽列 — 以裝置為單位的帳務概況 */
export interface AccountingOverviewRow {
  row_key: string;
  source: 'legacy_grant' | 'member_entitlement';
  principal_type: 'legacy_client' | 'member_device';
  device_id: string;
  member_account_id: string | number | null;
  user_id: string | number | null;
  brand: string;
  granted_mb: number;
  used_mb: number;
  remaining_mb: number;
  valid_until: string | null;
  /** 額度來源類型（兌換碼 / 後台發放 / 等級贈送 …） */
  source_types: string[];
  last_used_at: string | null;
}

/** VPN-74 統計卡資料 */
export interface AccountingOverviewStats {
  total_granted_mb: number;
  total_used_mb: number;
  total_remaining_mb: number;
  /** 即將到期裝置數 */
  expiring_soon_count: number;
  /** 異常裝置數（超量 / 多裝置共用等） */
  abnormal_device_count: number;
  /**
   * 過去 24h「扣量 fail-open」次數（grant 查詢失敗 + 扣帳失敗）。
   * >0 表示部分流量未落帳 → 「剩餘有效總量」為上界、可能高估（不可信區間）。
   */
  quota_fail_open_24h: number;
}

export interface AccountingOverviewResponse {
  rows: AccountingOverviewRow[];
  stats: AccountingOverviewStats;
  total: number;
}

export function overviewDetailUrl(row: AccountingOverviewRow): string | null {
  if (row.source === 'member_entitlement' &&
    (row.member_account_id == null || row.member_account_id === '')) return null;
  const params = new URLSearchParams({ from: 'overview', brand: row.brand });
  if (row.member_account_id != null && row.member_account_id !== '') {
    params.set('member_account_id', String(row.member_account_id));
  }
  if (row.source === 'legacy_grant' && row.device_id) {
    params.set('device_id', row.device_id);
  }
  return `/accounting/usage?${params.toString()}`;
}

export function resolveUsageDeepLinkBrand(
  params: URLSearchParams,
  allowedBrands: string[],
): { required: boolean; brand: string | null; error: boolean } {
  const brand = params.get('brand');
  const required = params.get('from') === 'overview' || brand !== null;
  return {
    required,
    brand,
    error: required && (!brand || brand === 'all' || !allowedBrands.includes(brand)),
  };
}

/** VPN-75 兌換 / 發放紀錄列 */
export interface AccountingGrantRow {
  id: number | string;
  device_id: string;
  user_id: string | number | null;
  member_account_id: string | number | null;
  promo_code: string | null;
  granted_mb: number;
  used_mb: number;
  remaining_mb: number;
  valid_until: string | null;
  status: string;
  expiration_reason?: "MEMBERSHIP_ISSUED";
  created_at: string;
  brand: string;
  source: "legacy_grant" | "member_entitlement";
  principal_type: "legacy_client" | "member_device";
  principal_id: string;
}

export interface AccountingGrantsResponse {
  rows: AccountingGrantRow[];
  total: number;
}

/** VPN-76-lite 流量使用明細 — 每用戶 upload / download / total + 連過的節點 */
export interface AccountingUsageNode {
  node_id: number | string;
  node_region: string;
  connected_at: string;
}

export interface AccountingUsageRow {
  /** Formal member identity shown in BO; null means legacy traffic is unlinked. */
  member_account_id: string | number | null;
  user_id: string | number | null;
  brand: string;
  /** Null means this historic reporter only supplied the total. */
  upload_bytes: number | null;
  /** Null means this historic reporter only supplied the total. */
  download_bytes: number | null;
  /** True when the aggregate also contains historic total-only usage. */
  has_total_only_usage: boolean;
  total_bytes: number;
  /** 按日彙總（groupBy=day）時的日期 'YYYY-MM-DD'；每用戶彙總模式為 null。 */
  bucket_date: string | null;
  nodes: AccountingUsageNode[];
  // 預留欄位（每用戶彙總層級後端尚未提供，前端顯示「預留」badge）。
  // service_category 已於「應用場景分析」分頁上線，此每用戶列不逐列細分。
  node_pool: string | null;
  quota_source: string | null;
  is_premium_quota: boolean | null;
  service_category: string | null;
}

export interface AccountingUsageResponse {
  rows: AccountingUsageRow[];
  total: number;
  effective_date_from?: string | null;
  effective_date_to?: string | null;
  date_range_defaulted?: boolean;
  /**
   * 裝置篩選是否為「該裝置所屬 user 的全部流量」近似。
   * 每用戶模式 + device → 讀 raw traffic session（per-device 精確）→ false；
   * 按日模式 + device → 無 device 維度 → user 級近似 → true，前端據此標示。
   */
  device_filter_approximate?: boolean;
}

export type UsageGroupBy = 'user' | 'day';

/** Default initial accounting window: yesterday through tomorrow in Taipei. */
export function defaultAccountingUsageDateRange(now: Date = new Date()): {
  dateFrom: string;
  dateTo: string;
} {
  const taipeiNow = new Date(now.getTime() + 8 * 60 * 60 * 1000);
  const dateFrom = new Date(taipeiNow.getTime() - 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  const dateTo = new Date(taipeiNow.getTime() + 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  return { dateFrom, dateTo };
}

/**
 * A grouping switch changes table columns before its replacement request has
 * returned. Never render rows from the previous grouping under new columns.
 */
export function visibleUsageRows<Row, Scope>(
  rows: Row[],
  loadedGroupBy: UsageGroupBy | null,
  currentGroupBy: UsageGroupBy,
  loadedScope: Scope | null,
  currentScope: Scope,
): Row[] {
  return loadedGroupBy === currentGroupBy && loadedScope === currentScope
    ? rows
    : [];
}

/**
 * Keeps only the newest asynchronous query authoritative for a view.
 *
 * Pagination and filter updates can overlap HTTP requests.  A response that
 * started before a newer query must not replace the current table state.
 */
export function createLatestRequestGuard(): {
  begin: () => number;
  isCurrent: (requestId: number) => boolean;
} {
  let latestRequestId = 0;
  return {
    begin: () => ++latestRequestId,
    isCurrent: requestId => requestId === latestRequestId,
  };
}

/* ───────── VPN-77 應用場景分析（對齊 /api/admin/traffic/*） ───────── */

/** service 分布 Top 列（對齊後端 ServiceTopRow） */
export interface TrafficServiceRow {
  service_category: string;
  service_name: string;
  upload_bytes: number;
  download_bytes: number;
  total_bytes: number;
  /** 占該品牌總流量百分比（0-100，1 位小數） */
  share_pct: number;
  session_count: number;
  /** 0=low, 1=medium, 2=high（該桶 MAX confidence） */
  confidence: number;
}

export interface TrafficServicesResponse {
  rows: TrafficServiceRow[];
  /** 全集合總流量 bytes（占比分母） */
  total_bytes: number;
  /** Unknown 桶占比（%，1 位小數） */
  unknown_pct: number;
  confidence_distribution: {
    high_pct: number;
    medium_pct: number;
    low_pct: number;
  };
  member_collection: {
    state: "unavailable";
    reason: "OBSERVATION_DISABLED";
  };
}

export interface TrafficActivityRow {
  source: "legacy_session" | "member_usage_window";
  activity_kind: "session" | "usage_window";
  member_account_id: number | null;
  user_id: number | null;
  principal_type: "legacy_client" | "member_account";
  principal_id: string;
  brand: string;
  node_id: number | null;
  /** 節點友善名稱；節點已移除或未命名時由畫面回退顯示 ID。 */
  node_name: string | null;
  stats_date: string | null;
  report_count: number;
  /** Directions are partial when this aggregate also includes old total-only reports. */
  has_total_only_usage: boolean;
  upload_bytes: number | null;
  download_bytes: number | null;
  total_bytes: number;
}

export interface TrafficActivitiesResponse {
  rows: TrafficActivityRow[];
  total: number;
  applied_date_from: string | null;
}

/** per-session 日彙總列（對齊後端 TrafficSessionRow） */
export interface TrafficSessionRow {
  brand: string;
  stats_date: string | null;
  user_id: number | null;
  node_id: number | null;
  /** 當前節點友善名稱；歷史節點不存在時由畫面回退顯示 ID。 */
  node_name: string | null;
  upload_bytes: number;
  download_bytes: number;
  total_bytes: number;
  session_count: number;
}

export interface TrafficSessionsResponse {
  rows: TrafficSessionRow[];
  total: number;
}

/* ───────── VPN-160 流量歸屬完整性（對齊 /traffic/attribution-integrity） ───────── */

export const TRAFFIC_ATTRIBUTION_REASONS = [
  'missing_user',
  'unknown_user',
  'ambiguous_user',
  'missing_node',
  'brand_scope_violation',
] as const;

export type TrafficAttributionReason =
  (typeof TRAFFIC_ATTRIBUTION_REASONS)[number];

/**
 * Session-quarantine aggregate row. It intentionally excludes brands, users,
 * raw identities, destinations, and flow diagnostics. `event_count` means
 * quarantined session count, not total ledger events.
 */
export interface TrafficAttributionIntegrityRow {
  reason: TrafficAttributionReason;
  node_id: number | null;
  upload_bytes: number;
  download_bytes: number;
  total_bytes: number;
  event_count: number;
}

export interface TrafficAttributionIntegrityResponse {
  /** Quarantined session bytes only; flow diagnostics are not traffic totals. */
  total_unattributed_bytes: number;
  /** Quarantined session count only. */
  total_session_count: number;
  rows: TrafficAttributionIntegrityRow[];
}

/**
 * Integrity ledger is cross-brand diagnostics: a full-scope admin view is the
 * only UI context permitted to fetch or render it. Keep this decision pure so
 * the no-request/no-render privacy boundary has direct regression coverage.
 */
export function canViewTrafficAttributionIntegrity(
  isAllBrandAdmin: boolean,
  scope: string,
): boolean {
  return isAllBrandAdmin && scope === 'all';
}

function nonNegativeNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function isTrafficAttributionReason(
  value: unknown,
): value is TrafficAttributionReason {
  return (
    typeof value === 'string' &&
    (TRAFFIC_ATTRIBUTION_REASONS as readonly string[]).includes(value)
  );
}

/**
 * 將 API 回應白名單化為畫面可用的 session-only aggregate 契約。
 *
 * 即使不預期的欄位被回傳，也不會穿透到 UI 的 table data；無效原因/數值的
 * 明細列直接捨棄，避免被誤呈現成品牌或用戶資料。
 */
export function normalizeAttributionIntegrityResponse(
  value: unknown,
): TrafficAttributionIntegrityResponse | null {
  if (!value || typeof value !== 'object') return null;
  const response = value as Record<string, unknown>;
  const totalUnattributedBytes = nonNegativeNumber(
    response.total_unattributed_bytes,
  );
  const totalSessionCount = nonNegativeNumber(response.total_session_count);
  if (
    totalUnattributedBytes == null ||
    totalSessionCount == null ||
    !Array.isArray(response.rows)
  ) {
    return null;
  }

  const rows: TrafficAttributionIntegrityRow[] = [];
  for (const rawRow of response.rows) {
    if (!rawRow || typeof rawRow !== 'object') continue;
    const row = rawRow as Record<string, unknown>;
    const nodeId =
      row.node_id == null ? null : nonNegativeNumber(row.node_id);
    const uploadBytes = nonNegativeNumber(row.upload_bytes);
    const downloadBytes = nonNegativeNumber(row.download_bytes);
    const totalBytes = nonNegativeNumber(row.total_bytes);
    const eventCount = nonNegativeNumber(row.event_count);
    if (
      !isTrafficAttributionReason(row.reason) ||
      nodeId === null && row.node_id != null ||
      uploadBytes == null ||
      downloadBytes == null ||
      totalBytes == null ||
      eventCount == null
    ) {
      continue;
    }
    rows.push({
      reason: row.reason,
      node_id: nodeId,
      upload_bytes: uploadBytes,
      download_bytes: downloadBytes,
      total_bytes: totalBytes,
      event_count: eventCount,
    });
  }

  return {
    total_unattributed_bytes: totalUnattributedBytes,
    total_session_count: totalSessionCount,
    rows,
  };
}

/** quarantine 原因的營運顯示文案；不推測任何品牌或用戶身份。 */
export function describeTrafficAttributionReason(reason: TrafficAttributionReason): {
  label: string;
  color: string;
} {
  const descriptions: Record<
    TrafficAttributionReason,
    { label: string; color: string }
  > = {
    missing_user: { label: '未提供用戶', color: 'orange' },
    unknown_user: { label: '找不到用戶', color: 'orange' },
    ambiguous_user: { label: '用戶無法唯一判定', color: 'gold' },
    missing_node: { label: '找不到節點', color: 'red' },
    brand_scope_violation: { label: '節點品牌範圍不符', color: 'red' },
  };
  return descriptions[reason];
}

/* ───────── Telemetry delivery status（對齊 /traffic/delivery-status） ───────── */

/**
 * Per-node delivery projection.  This is deliberately limited to the safe
 * operational fields in the API contract: no traffic identity, destination,
 * account, IP, report ID, signature, or secret may enter this UI model.
 */
export interface TrafficDeliveryStatusRow {
  node_id: number;
  node_name: string | null;
  last_received_at: string | null;
  last_heartbeat_at: string | null;
  last_detail_accepted_at: string | null;
  last_snapshot_at: string | null;
  sidecar_version: string | null;
  queue_depth: number;
  queued_bytes: number;
  dropped_report_count: number;
  dropped_record_count: number;
  last_disposition: TrafficDeliveryDisposition | null;
  last_reject_reason: string | null;
  is_stale: boolean;
}

export interface TrafficDeliveryStatusResponse {
  rows: TrafficDeliveryStatusRow[];
}

export const TRAFFIC_DELIVERY_DISPOSITIONS = [
  'accepted',
  'duplicate',
  'rejected',
  'retryable_failure',
] as const;

export type TrafficDeliveryDisposition =
  (typeof TRAFFIC_DELIVERY_DISPOSITIONS)[number];

/**
 * Delivery status is a cross-brand operational diagnostic.  Requiring the
 * all-brand *scope* as well as the all-brand identity also ensures authFetch
 * omits X-Brand, so this endpoint is never accidentally queried as a
 * single-brand read.
 */
export function canViewTrafficDeliveryStatus(
  isAllBrandAdmin: boolean,
  scope: string,
): boolean {
  return isAllBrandAdmin && scope === 'all';
}

function isTrafficDeliveryDisposition(
  value: unknown,
): value is TrafficDeliveryDisposition {
  return (
    typeof value === 'string' &&
    (TRAFFIC_DELIVERY_DISPOSITIONS as readonly string[]).includes(value)
  );
}

function isSafeTimestamp(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length <= 64 &&
    Number.isFinite(Date.parse(value))
  );
}

/** Version strings are operational metadata, not a free-form diagnostic. */
function isSafeSidecarVersion(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^[A-Za-z0-9._+-]{1,64}$/.test(value)
  );
}

/** Stable machine reason codes only; free-form backend errors are not UI data. */
function isSafeRejectReason(value: unknown): value is string {
  return typeof value === 'string' && /^[a-z][a-z0-9_]{0,63}$/.test(value);
}

/** Node names are server-managed labels; cap their size before displaying. */
function safeNodeName(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 && value.length <= 128
    ? value
    : null;
}

function safeOptionalTimestamp(value: unknown): string | null {
  return value == null ? null : isSafeTimestamp(value) ? value : null;
}

function safeOptionalVersion(value: unknown): string | null {
  return value == null ? null : isSafeSidecarVersion(value) ? value : null;
}

function safeOptionalRejectReason(value: unknown): string | null {
  return value == null ? null : isSafeRejectReason(value) ? value : null;
}

/**
 * Whitelist the status response before it reaches Table data.  Invalid rows
 * are ignored rather than becoming an opportunity to surface accidental
 * backend diagnostics.  This is intentionally separate from unwrap(): the
 * latter deals only with the standard API envelope.
 */
export function normalizeTrafficDeliveryStatusResponse(
  value: unknown,
): TrafficDeliveryStatusResponse | null {
  if (!value || typeof value !== 'object') return null;
  const response = value as Record<string, unknown>;
  if (!Array.isArray(response.rows)) return null;

  const rows: TrafficDeliveryStatusRow[] = [];
  for (const rawRow of response.rows) {
    if (!rawRow || typeof rawRow !== 'object') continue;
    const row = rawRow as Record<string, unknown>;
    const nodeId = nonNegativeNumber(row.node_id);
    const queueDepth = nonNegativeNumber(row.queue_depth);
    const queuedBytes = nonNegativeNumber(row.queued_bytes);
    const droppedReportCount = nonNegativeNumber(row.dropped_report_count);
    const droppedRecordCount = nonNegativeNumber(row.dropped_record_count);
    if (
      nodeId == null ||
      !Number.isSafeInteger(nodeId) ||
      queueDepth == null ||
      !Number.isSafeInteger(queueDepth) ||
      queuedBytes == null ||
      !Number.isSafeInteger(queuedBytes) ||
      droppedReportCount == null ||
      !Number.isSafeInteger(droppedReportCount) ||
      droppedRecordCount == null ||
      !Number.isSafeInteger(droppedRecordCount) ||
      typeof row.is_stale !== 'boolean'
    ) {
      continue;
    }

    rows.push({
      node_id: nodeId,
      node_name: safeNodeName(row.node_name),
      last_received_at: safeOptionalTimestamp(row.last_received_at),
      last_heartbeat_at: safeOptionalTimestamp(row.last_heartbeat_at),
      last_detail_accepted_at: safeOptionalTimestamp(
        row.last_detail_accepted_at,
      ),
      last_snapshot_at: safeOptionalTimestamp(row.last_snapshot_at),
      sidecar_version: safeOptionalVersion(row.sidecar_version),
      queue_depth: queueDepth,
      queued_bytes: queuedBytes,
      dropped_report_count: droppedReportCount,
      dropped_record_count: droppedRecordCount,
      last_disposition: isTrafficDeliveryDisposition(row.last_disposition)
        ? row.last_disposition
        : null,
      last_reject_reason: safeOptionalRejectReason(row.last_reject_reason),
      is_stale: row.is_stale,
    });
  }

  return { rows };
}

export function describeTrafficDeliveryDisposition(
  disposition: TrafficDeliveryDisposition | null,
): { label: string; color: string } {
  const descriptions: Record<
    TrafficDeliveryDisposition,
    { label: string; color: string }
  > = {
    accepted: { label: '已接受', color: 'green' },
    duplicate: { label: '重複（已接受）', color: 'blue' },
    rejected: { label: '已拒收', color: 'red' },
    retryable_failure: { label: '可重試失敗', color: 'orange' },
  };
  return disposition
    ? descriptions[disposition]
    : { label: '尚無回執', color: 'default' };
}

/** VPN-122 待補分類候選 domain（對齊 /api/admin/traffic/classification-candidates） */
export interface TrafficClassificationCandidateRow {
  host: string;
  brand: string;
  sample_count: number;
  first_sampled_at: string | null;
  last_sampled_at: string | null;
}

export interface TrafficClassificationCandidatesResponse {
  rows: TrafficClassificationCandidateRow[];
}

export const SERVICE_CATEGORIES = [
  "video",
  "ai",
  "social",
  "browse",
  "download",
  "game",
  "unknown",
] as const;

export type ServiceCategory = (typeof SERVICE_CATEGORIES)[number];

export interface TrafficClassifyCandidateResponse {
  host: string;
  brand: string;
  service_category: ServiceCategory;
  rule_id: number;
  rule_created: boolean;
  cleared_sample_count: number;
}

/** 服務類型 → 中文標籤 + Tag 顏色（對齊後端 SERVICE_CATEGORIES） */
export function describeServiceCategory(category: string): {
  label: string;
  color: string;
} {
  const map: Record<string, { label: string; color: string }> = {
    video: { label: "影音", color: "magenta" },
    ai: { label: "AI", color: "geekblue" },
    social: { label: "社群", color: "cyan" },
    browse: { label: "瀏覽", color: "green" },
    download: { label: "下載", color: "gold" },
    game: { label: "遊戲", color: "volcano" },
    unknown: { label: "未歸因", color: "default" },
  };
  return map[category] ?? { label: category || "未歸因", color: "default" };
}

/** confidence tinyint（0/1/2）→ 中文標籤 + 顏色。 */
export function describeConfidence(c: number | null | undefined): {
  label: string;
  color: string;
} {
  const n = Number(c);
  if (n >= 2) return { label: "高", color: "green" };
  if (n === 1) return { label: "中", color: "orange" };
  return { label: "低", color: "red" };
}

/* ───────── 格式化工具 ───────── */

/** MB 數值人性化：>= 1024 顯示 GB，否則 MB；null/undefined →「-」 */
export function formatMb(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return "-";
  const v = Number(value);
  if (v >= 1024) {
    const gb = v / 1024;
    return `${Number.isInteger(gb) ? gb : gb.toFixed(2)} GB`;
  }
  return `${Number.isInteger(v) ? v : v.toFixed(2)} MB`;
}

/** bytes 人性化（B / KB / MB / GB / TB），對齊 nodes 頁 formatBytes 範式 */
export function formatBytes(bytes: number | null | undefined): string {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.min(
    Math.floor(Math.log(n) / Math.log(1024)),
    units.length - 1,
  );
  const value = n / Math.pow(1024, i);
  return `${i === 0 ? value : value.toFixed(2)} ${units[i]}`;
}

/* ───────── 伺服器端 CSV 下載（?format=csv，遵守品牌權限） ───────── */

/**
 * 觸發伺服器端 CSV 匯出下載。
 *
 * 走 authFetch（自動帶 Authorization + X-Brand header），所以匯出範圍
 * 由後端依品牌權限決定 —— 一般 admin 只拿到自己品牌、全品牌 admin 依
 * `brand` 參數彙總，與表格資料一致。失敗時 throw，由呼叫端顯示訊息。
 *
 * @param path   API 路徑（如 /api/admin/accounting/overview）
 * @param params 與表格相同的查詢參數（會自動補上 format=csv）
 * @param filename 下載檔名（不含副檔名）
 */
export async function downloadAccountingCsv(
  path: string,
  params: URLSearchParams,
  filename: string,
): Promise<void> {
  const query = new URLSearchParams(params);
  query.set("format", "csv");
  const res = await authFetch(`${path}?${query.toString()}`);
  if (!res.ok) {
    throw new Error(`匯出失敗（HTTP ${res.status}）`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ───────── 共用 helper ───────── */

/** 後端統一回 { code, data } 包裝；容錯讀出 data（少數情況直接回 body） */
export function unwrap<T>(result: unknown): T | null {
  if (result && typeof result === "object") {
    const r = result as { code?: number; data?: unknown };
    if (r.code === 0 && r.data != null) return r.data as T;
    // 容錯：未包裝時直接回 body
    if (r.code === undefined) return result as T;
  }
  return null;
}

/** 兌換 / 發放狀態 → 中文標籤 + Tag 顏色 */
export function describeGrantStatus(status: string): {
  label: string;
  color: string;
} {
  const map: Record<string, { label: string; color: string }> = {
    active: { label: "有效", color: "green" },
    valid: { label: "有效", color: "green" },
    expired: { label: "已過期", color: "default" },
    exhausted: { label: "已用罄", color: "orange" },
    depleted: { label: "已用罄", color: "orange" },
    disabled: { label: "已停用", color: "red" },
    revoked: { label: "已撤銷", color: "red" },
    pending: { label: "待生效", color: "blue" },
  };
  return map[status] ?? { label: status || "-", color: "default" };
}

/** 額度來源類型 → 中文標籤 */
export function describeSourceType(type: string): string {
  const map: Record<string, string> = {
    promo: "兌換碼",
    promo_code: "兌換碼",
    redeem: "兌換碼",
    admin: "後台發放",
    admin_grant: "後台發放",
    manual: "後台發放",
    tier: "等級贈送",
    tier_grant: "等級贈送",
    subscription: "訂閱",
  };
  return map[type] ?? type;
}
